<template>
    <div v-if="totalPages > 1" class="pagination">
        <div class="pagination__controls">
            <button
                class="pagination__button"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
            >
                &lt;
            </button>

            <div v-for="page in displayedPages">
                <button
                    v-if="page !== '...'"
                    :key="page"
                    :class="[
                        'pagination__button',
                        { pagination__button_active: page === currentPage },
                    ]"
                    @click="goToPage(page as number)"
                >
                    {{ page }}
                </button>
                <span v-else class="pagination__ellipsis">...</span>
            </div>

            <button
                class="pagination__button"
                :disabled="currentPage === totalPages"
                @click="goToPage(currentPage + 1)"
            >
                &gt;
            </button>
        </div>

        <div class="pagination__items-per-page">
            <span>На страницу:</span>
            <input
                v-model.number="localPage"
                class="pagination__input"
                @blur="updatePage"
            />
            <UISimpleButton
                button-type="outlined"
                @clicked="updatePage"
            >
                Перейти
            </UISimpleButton>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, defineProps, defineEmits, computed, watch } from 'vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    const props = defineProps({
        totalPages: {
            type: Number,
            required: true,
        },
        currentPage: {
            type: Number,
            required: true,
        },
        itemsPerPage: {
            type: Number,
            required: true,
        },
    });

    const emit = defineEmits<{
        (e: 'page-change', page: number): void,
        (e: 'items-per-page-change', itemsPerPage: number): void,
    }>();

    const localPage = ref(props.currentPage);
    const localItemsPerPage = ref(props.itemsPerPage);

    const displayedPages = computed(() => {
        const pages: (number | string)[] = [];
        const { totalPages, currentPage } = props;

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        }
        else if (currentPage <= 4) {
            pages.push(1, 2, 3, 4, 5, '...', totalPages);
        }
        else if (currentPage >= totalPages - 3) {
            pages.push(
                1,
                '...',
                totalPages - 4,
                totalPages - 3,
                totalPages - 2,
                totalPages - 1,
                totalPages,
            );
        }
        else {
            pages.push(
                1,
                '...',
                currentPage - 1,
                currentPage,
                currentPage + 1,
                '...',
                totalPages,
            );
        }

        return pages;
    });

    watch(
        () => props.currentPage,
        (newPage) => {
            localPage.value = newPage;
        },
    );

    watch(
        () => props.itemsPerPage,
        (newItemsPerPage) => {
            localItemsPerPage.value = newItemsPerPage;
        },
    );

    function updatePage(): void {
        if (localPage.value > 0 && localPage.value <= props.totalPages) {
            emit('page-change', localPage.value);
        }
        else {
            localPage.value = props.currentPage;
        }
    }

    function goToPage(page: number): void {
        if (page > 0 && page <= props.totalPages) {
            emit('page-change', page);
        }
    }
</script>

<style lang="scss" scoped>
    @use './TablePagination.scss' as *;
</style>
